// Tier 2 — Loan Amortisation engine.
// Pure functions. Consumes Tier 1 affordability outputs (eligible_loan, rate, tenure, EMI).
// No React, no state. Deterministic for the same inputs.

// ── Helpers ────────────────────────────────────────────────────────
function monthsToYM(totalMonths) {
    if (!isFinite(totalMonths) || totalMonths <= 0) return { years: 0, months: 0, label: "0 mo" };
    const y = Math.floor(totalMonths / 12);
    const m = Math.round(totalMonths - y * 12);
    if (m === 12) return { years: y + 1, months: 0, label: `${y + 1} yrs` };
    if (y === 0) return { years: 0, months: m, label: `${m} mo` };
    if (m === 0) return { years: y, months: 0, label: `${y} yrs` };
    return { years: y, months: m, label: `${y} yrs ${m} mo` };
}

// ── Tier 2 · Loan amortisation composite ───────────────────────────
// Inputs come pre-derived from Tier 1 to avoid duplicating affordability logic.
// principal = eligible_loan, emi = emi_on_eligible_loan.
export function computeLoanAmortisation(inputs) {
    const {
        principal = 0,            // L · loan amount from Tier 1 (eligible_loan)
        rate = 8.5,               // annual %
        tenure = 20,              // years
        emi = 0,                  // monthly EMI from Tier 1 (emi_on_eligible_loan)
        salary = 0,               // for S_loan affordability check
    } = inputs || {};

    const monthlyRate = (rate / 100) / 12;
    const totalMonths = Math.round(tenure * 12);

    // L1 · month-by-month simulation → roll up into years.
    // We track cumulative principal paid so equity milestones (25/50/75/100%)
    // can be interpolated to month-precision.
    const yearly = [];
    const milestoneTargets = [
        { pct: 25, target: principal * 0.25 },
        { pct: 50, target: principal * 0.50 },
        { pct: 75, target: principal * 0.75 },
        { pct: 100, target: principal },
    ];
    const milestones = milestoneTargets.map((m) => ({ pct: m.pct, monthsToReach: null }));

    let balance = principal;
    let cumPrincipal = 0;
    let cumInterest = 0;

    // Per-year accumulators.
    for (let y = 1; y <= tenure; y++) {
        let yPrincipal = 0;
        let yInterest = 0;
        for (let mo = 1; mo <= 12; mo++) {
            const monthIndex = (y - 1) * 12 + mo; // 1-based total month
            if (monthIndex > totalMonths) break;
            const interestPart = balance * monthlyRate;
            let principalPart = emi - interestPart;
            // Guard the final month against floating-point overshoot.
            if (principalPart > balance) principalPart = balance;
            const prevCum = cumPrincipal;
            balance -= principalPart;
            cumPrincipal += principalPart;
            cumInterest += interestPart;
            yPrincipal += principalPart;
            yInterest += interestPart;

            // Check milestones — linear interpolate inside the month it crosses.
            milestones.forEach((m, i) => {
                if (m.monthsToReach == null && cumPrincipal >= milestoneTargets[i].target && principal > 0) {
                    const need = milestoneTargets[i].target - prevCum;
                    const frac = principalPart > 0 ? need / principalPart : 0;
                    m.monthsToReach = (monthIndex - 1) + Math.min(1, Math.max(0, frac));
                }
            });

            if (balance < 0) balance = 0;
        }
        yearly.push({
            year: y,
            principal: yPrincipal,
            interest: yInterest,
            total: yPrincipal + yInterest,
            balance: Math.max(0, balance),
        });
    }

    // L2 · Totals (re-derive for safety; will match Tier 1 within rounding).
    const total_principal = cumPrincipal;
    const total_interest = cumInterest;
    const total_outflow = total_principal + total_interest;
    const interest_ratio = total_outflow > 0 ? total_interest / total_outflow : 0;
    const principal_pct = total_outflow > 0 ? Math.round((total_principal / total_outflow) * 100) : 0;
    const interest_pct = total_outflow > 0 ? 100 - principal_pct : 0;

    // L3 · Equity milestones with month-precision labels.
    const equity_milestones = milestones.map((m) => ({
        pct: m.pct,
        months: m.monthsToReach,
        label: m.monthsToReach == null ? "—" : monthsToYM(m.monthsToReach).label,
    }));

    // L4 · EMI-to-salary ratio (drives S_loan).
    const emi_to_salary_pct = salary > 0 ? (emi / salary) * 100 : 0;

    // L5 · Loan sub-score (S_loan). 60 base + 4 components, cap 100.
    //   • Interest ratio below 0.50 → favourable repayment profile.
    //   • EMI under 35% of salary → headroom remains for living.
    //   • Tenure ≤ 20 yrs → shorter total interest exposure.
    //   • Rate ≤ 8.5% → at or below mainstream lender benchmark.
    const interestRatioGood = interest_ratio > 0 && interest_ratio < 0.50;
    const emiToSalaryGood = salary > 0 && emi_to_salary_pct < 35;
    const tenureGood = tenure > 0 && tenure <= 20;
    const rateGood = rate > 0 && rate <= 8.5;
    const loan_score = Math.min(
        100,
        60 +
        (interestRatioGood ? 10 : 0) +
        (emiToSalaryGood ? 10 : 0) +
        (tenureGood ? 10 : 0) +
        (rateGood ? 10 : 0)
    );

    return {
        // Echo inputs for downstream consumers.
        principal, rate, tenure, emi, salary,
        // L1 — schedule
        schedule: yearly,
        // L2 — totals
        total_principal,
        total_interest,
        total_outflow,
        interest_ratio,
        principal_pct,
        interest_pct,
        // L3 — milestones
        equity_milestones,
        // L4 — affordability check
        emi_to_salary_pct,
        // L5 — score
        loan_score,
    };
}

export { monthsToYM };

// ── Tier 2 · Prepayment impact ─────────────────────────────────────
// Re-amortises the loan with an extra prepayment applied at the chosen cadence
// and returns the tenure cut + interest saved versus the baseline.
//   frequency: "One-time" | "Annual" | "Monthly extra"
//   lump     : prepayment amount per occurrence (₹)
export function computePrepaymentImpact(inputs) {
    const {
        principal = 0,
        rate = 8.5,
        tenure = 20,
        emi = 0,
        lump = 0,
        frequency = "Annual",
    } = inputs || {};

    const m = (rate / 100) / 12;
    const baselineMonths = Math.round(tenure * 12);
    const baselineInterest = Math.max(0, emi * baselineMonths - principal);

    if (principal <= 0 || emi <= 0 || lump <= 0) {
        return {
            new_tenure_months: baselineMonths,
            tenure_cut_months: 0,
            interest_saved: 0,
            new_total_interest: baselineInterest,
            baseline_total_interest: baselineInterest,
            lump, frequency,
        };
    }

    let balance = principal;
    let interestPaid = 0;
    let monthsTaken = 0;

    for (let month = 1; month <= baselineMonths; month++) {
        if (balance <= 0) break;
        // Regular EMI split
        const interest = balance * m;
        let principalPart = emi - interest;
        if (principalPart > balance) principalPart = balance;
        balance -= principalPart;
        interestPaid += interest;

        // Apply prepayment per cadence (after the EMI for that month).
        if (balance > 0) {
            let prepay = 0;
            if (frequency === "Monthly extra") prepay = lump;
            else if (frequency === "Annual" && month % 12 === 0) prepay = lump;
            else if (frequency === "One-time" && month === 1) prepay = lump;
            if (prepay > 0) balance -= Math.min(prepay, balance);
        }
        monthsTaken = month;
        if (balance <= 0) break;
    }

    const tenure_cut_months = Math.max(0, baselineMonths - monthsTaken);
    const interest_saved = Math.max(0, baselineInterest - interestPaid);

    return {
        new_tenure_months: monthsTaken,
        tenure_cut_months,
        interest_saved,
        new_total_interest: interestPaid,
        baseline_total_interest: baselineInterest,
        lump, frequency,
    };
}
